import axios, { AxiosInstance } from 'axios';
import { envConfig } from '@/config/env.config';
import {
  Company,
  CreateInterviewRequest,
  CreateInterviewResponse,
} from '@/types/interview.types';

const TOKEN_STORAGE_KEY = 'interview_builder_token';

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * API client for the Interview Builder
 */
class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: envConfig.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth interceptor to include JWT token
    this.client.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get stored JWT token
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Store JWT token
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }

  /**
   * Clear stored JWT token
   */
  clearToken(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Check if user has a valid token stored
   */
  hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Authenticate with the backend
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>(
      '/interview/builder/auth',
      { email, password }
    );
    this.setToken(response.data.accessToken);
    return response.data;
  }

  /**
   * Logout - clear stored token
   */
  logout(): void {
    this.clearToken();
  }

  /**
   * Get list of companies for the dropdown
   */
  async getCompanies(): Promise<Company[]> {
    const response = await this.client.get<Company[]>(
      '/interview/builder/companies'
    );
    return response.data;
  }

  /**
   * Create an interview from the builder
   */
  async createInterview(
    data: CreateInterviewRequest
  ): Promise<CreateInterviewResponse> {
    const response = await this.client.post<CreateInterviewResponse>(
      '/interview/builder',
      data
    );
    return response.data;
  }
}

export const apiService = new ApiService();
